import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, Nav, Tab, Row, Col, Alert, Spinner, Button, Form, InputGroup } from 'react-bootstrap';
import { faUser, faBank, faUndo, faTruck, faIdCard, faBook, faCopy, faShareAlt, faEnvelope, faExternalLinkAlt, faEdit, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_SELLER_PROFILE } from 'api/graphql/mutations/seller/updateProfile';
import { GET_SELLER_PROFILE } from 'api/graphql/queries/getSellerProfileQuery';
import { useAuth } from 'providers/AuthProvider';
import { fileToBase64 } from 'utils/imageUtils';
import DocumentUploadSection from '../../../components/seller/DocumentUploadSection';

// Import form components
import SellerProfileInfo from 'components/forms/SellerProfileInfo';
import SellerBankInfo from 'components/forms/SellerBankInfo';
import SellerReturnPolicy from 'components/forms/SellerReturnPolicy';
import SellerShippingPolicy from 'components/forms/SellerShippingPolicy';

interface TabItem {
  id: string;
  label: string;
  icon: IconProp;
  content: React.ReactNode;
}

interface SellerProfileEditorProps {
  onUpdateSellerInfo?: (updatedTitle: string, updatedDescription: string) => void;
  onProfileUpdated?: () => void;
}

const SellerProfileEditorWithDocs: React.FC<SellerProfileEditorProps> = ({ 
  onUpdateSellerInfo,
  onProfileUpdated 
}) => {
  const { t } = useTranslation();
  const { seller, setSeller } = useAuth();

  // State to track only the fields that have been modified
  const [modifiedFields, setModifiedFields] = useState<Record<string, any>>({});
  
  // State to track which tab is in edit mode (empty string means none)
  const [editingTab, setEditingTab] = useState<string>('');
  
  // Store original values for reset functionality
  const [originalValues, setOriginalValues] = useState<Record<string, any>>({});
  
  // Component-specific form states
  const [formState, setFormState] = useState<Record<string, any>>({
    // General info
    shop_title: seller?.shop_title || '',
    name: seller?.name || '',
    shop_url: seller?.shop_url || '',
    taxvat: seller?.taxvat || '',
    company_description: seller?.company_description || '',
    
    // Contact & Address
    email: seller?.email || '',
    contact_number: seller?.contact_number || seller?.billing_telephone || '',
    billing_street: seller?.billing_street || '',
    billing_city: seller?.billing_city || '',
    billing_region: seller?.billing_region || '',
    billing_postcode: seller?.billing_postcode || '',
    billing_country_id: seller?.billing_country_id || '',
    
    // Social media
    twitter_id: seller?.twitter_id || '',
    facebook_id: seller?.facebook_id || '',
    instagram_id: seller?.instagram_id || '',
    youtube_id: seller?.youtube_id || '',
    pinterest_id: seller?.pinterest_id || '',
    
    // Policies
    return_policy: seller?.return_policy || '',
    shipping_policy: seller?.shipping_policy || '',
    privacy_policy: seller?.privacy_policy || ''
  });
  
  // Function to map document URLs from seller profile to the format expected by DocumentUploadSection
  const getDocumentUrls = useCallback((): Record<string, string> => {
    if (!seller) return {};
    
    const mappedDocs: Record<string, string> = {};
    
    // Map document URLs with the field naming convention that DocumentUploadSection expects
    if (seller.contract_doc) mappedDocs.contract = seller.contract_doc;
    if (seller.patente_doc) mappedDocs.patente = seller.patente_doc;
    if (seller.rne_doc) mappedDocs.rne = seller.rne_doc;
    if (seller.cin_recto_doc) mappedDocs.cinRecto = seller.cin_recto_doc;
    if (seller.cin_verso_doc) mappedDocs.cinVerso = seller.cin_verso_doc;
    if (seller.rib_doc) mappedDocs.rib = seller.rib_doc;
    
    return mappedDocs;
  }, [seller]);
  
  // Function to map validation statuses from seller profile
  const getValidationStatuses = useCallback((): Record<string, string> => {
    if (!seller) return {};
    
    const mappedValidations: Record<string, string> = {};
    
    // Map validation statuses
    if (seller.contract_doc_validation) mappedValidations.contract = seller.contract_doc_validation;
    if (seller.patente_doc_validation) mappedValidations.patente = seller.patente_doc_validation;
    if (seller.rne_doc_validation) mappedValidations.rne = seller.rne_doc_validation;
    if (seller.cin_recto_doc_validation) mappedValidations.cinRecto = seller.cin_recto_doc_validation;
    if (seller.cin_verso_doc_validation) mappedValidations.cinVerso = seller.cin_verso_doc_validation;
    if (seller.rib_doc_validation) mappedValidations.rib = seller.rib_doc_validation;
    
    return mappedValidations;
  }, [seller]);

  // State for feedback
  const [feedback, setFeedback] = useState<{
    show: boolean;
    type: 'success' | 'danger' | 'warning';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: ''
  });

  // Update formState whenever seller data changes, but only when not in edit mode
  useEffect(() => {
    // Only update the form state from seller data when not in editing mode
    // This prevents overwriting user input while they're editing
    if (seller && !editingTab) {
      setFormState({
        // General info
        shop_title: seller.shop_title || '',
        name: seller.name || '',
        shop_url: seller.shop_url || '',
        taxvat: seller.taxvat || '',
        company_description: seller.company_description || '',
        
        // Contact & Address
        email: seller.email || '',
        contact_number: seller.contact_number || seller.billing_telephone || '',
        billing_street: seller.billing_street || '',
        billing_city: seller.billing_city || '',
        billing_region: seller.billing_region || '',
        billing_postcode: seller.billing_postcode || '',
        billing_country_id: seller.billing_country_id || '',
        
        // Social media
        twitter_id: seller.twitter_id || '',
        facebook_id: seller.facebook_id || '',
        instagram_id: seller.instagram_id || '',
        youtube_id: seller.youtube_id || '',
        pinterest_id: seller.pinterest_id || '',
        
        // Policies
        return_policy: seller.return_policy || '',
        shipping_policy: seller.shipping_policy || '',
        privacy_policy: seller.privacy_policy || ''
      });
    }
  }, [seller, editingTab]);

  // Query to get fresh profile data
  const { refetch } = useQuery(GET_SELLER_PROFILE, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // GraphQL mutation for updating the profile
  const [updateProfile, { loading: isSubmitting }] = useMutation(UPDATE_SELLER_PROFILE, {
    onCompleted: (data) => {
      if (data.updateSellerProfile.success) {
        setFeedback({
          show: true,
          type: 'success',
          message: t('updateSuccess')
        });

        // Exit edit mode
        setEditingTab('');

        // Clear the modified fields after successful update
        setModifiedFields({});

        // Refetch profile data to ensure we have the latest version
        refetch();
        
        // Notify parent component about the profile update
        if (onProfileUpdated) {
          onProfileUpdated();
        }

        // Auto-hide feedback after delay
        setTimeout(() => {
          setFeedback(prev => ({ ...prev, show: false }));
        }, 3000);
      } else {
        setFeedback({
          show: true,
          type: 'danger',
          message: data.updateSellerProfile.message || t('updateError')
        });
      }
    },
    onError: (error) => {
      console.error('Error updating profile:', error);
      setFeedback({
        show: true,
        type: 'danger',
        message: error.message || t('updateError')
      });
    }
  });

  // Start editing a tab
  const startEditing = (tabId: string) => {
    // Store original values for potential reset
    if (tabId === 'general-info') {
      setOriginalValues({
        shop_title: formState.shop_title,
        name: formState.name,
        taxvat: formState.taxvat,
        company_description: formState.company_description
      });
    } else if (tabId === 'contact-address') {
      setOriginalValues({
        email: formState.email,
        contact_number: formState.contact_number,
        billing_street: formState.billing_street,
        billing_city: formState.billing_city,
        billing_region: formState.billing_region,
        billing_postcode: formState.billing_postcode,
        billing_country_id: formState.billing_country_id
      });
    } else if (tabId === 'social-media') {
      setOriginalValues({
        twitter_id: formState.twitter_id,
        facebook_id: formState.facebook_id,
        instagram_id: formState.instagram_id,
        youtube_id: formState.youtube_id,
        pinterest_id: formState.pinterest_id
      });
    } else if (tabId === 'policies') {
      setOriginalValues({
        return_policy: formState.return_policy,
        shipping_policy: formState.shipping_policy,
        privacy_policy: formState.privacy_policy
      });
    }
    
    setEditingTab(tabId);
  };

  // Cancel editing and reset values
  const cancelEditing = () => {
    // Restore original values
    setFormState(prev => ({
      ...prev,
      ...originalValues
    }));
    
    // Clear modified fields for this tab
    setModifiedFields({});
    
    // Exit edit mode
    setEditingTab('');
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Update the form state
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Track in modified fields
    setModifiedFields(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handler for document uploads - update modified fields with document URLs
  const handleDocumentsUploaded = (newDocuments: Record<string, string>) => {
    console.log('Documents uploaded:', newDocuments);
    
    // Create a map from document field names to the seller profile field names
    const docFieldMap: Record<string, string> = {
      contract: 'contract_doc',
      patente: 'patente_doc',
      rne: 'rne_doc',
      cinRecto: 'cin_recto_doc',
      cinVerso: 'cin_verso_doc',
      rib: 'rib_doc'
    };
    
    // Add uploaded documents to modified fields
    Object.entries(newDocuments).forEach(([docType, url]) => {
      const fieldName = docFieldMap[docType];
      if (fieldName) {
        setModifiedFields(prev => ({
          ...prev,
          [fieldName]: url
        }));
      }
    });
  };

  // Validate and save changes for the current tab
  const saveChanges = async () => {
    // If no changes, don't submit
    if (Object.keys(modifiedFields).length === 0) {
      setFeedback({
        show: true,
        type: 'warning',
        message: t('noChangesToSubmit')
      });
      return;
    }

    try {
      // Make a copy to process
      const input: Record<string, any> = { ...modifiedFields };

      // Remove fields not supported by the backend schema
      const unsupportedFields = ['taxvat']; // privacy_policy is now supported by the backend
      unsupportedFields.forEach(field => {
        if (field in input) {
          delete input[field];
          console.log(`Removed ${field} field from input as it is not supported by the backend schema`);
        }
      });

      // Process any file uploads (convert to base64)
      for (const [key, value] of Object.entries(input)) {
        if (value instanceof File) {
          input[key] = await fileToBase64(value);
        }
      }

      // Update local state first for optimistic UI update
      if (setSeller && seller) {
        const updatedSeller = {
          ...seller,
          ...modifiedFields
        };

        // For file objects, use the file name for display
        for (const [key, value] of Object.entries(modifiedFields)) {
          if (value instanceof File) {
            updatedSeller[key] = value.name;
          }
        }

        setSeller(updatedSeller);
        localStorage.setItem('seller', JSON.stringify(updatedSeller));
      }

      // Submit the GraphQL mutation with only changed fields (excluding unsupported fields)
      await updateProfile({
        variables: {
          input
        }
      });

      // If seller info was updated and callback provided, call it
      if (onUpdateSellerInfo &&
        (modifiedFields.shop_title !== undefined || modifiedFields.company_description !== undefined)) {
        onUpdateSellerInfo(
          modifiedFields.shop_title !== undefined ? modifiedFields.shop_title : seller?.shop_title || '',
          modifiedFields.company_description !== undefined ? modifiedFields.company_description : seller?.company_description || ''
        );
      }
    } catch (error) {
      console.error('Error processing profile update:', error);
      setFeedback({
        show: true,
        type: 'danger',
        message: t('updateError')
      });
    }
  };

  // Define tab items with child components and their callbacks
  const tabItems: TabItem[] = [
    {
      id: "general-info",
      label: "GENERAL INFORMATION",
      icon: faUser,
      content: (
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Basic Information</h5>
            {editingTab !== 'general-info' ? (
              <Button 
                variant="outline-warning"
                size="sm"
                onClick={() => startEditing('general-info')}
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary"
                  size="sm"
                  onClick={cancelEditing}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
                <Button 
                  variant="warning"
                  size="sm"
                  onClick={saveChanges}
                  disabled={Object.keys(modifiedFields).length === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                  )}
                  Save
                </Button>
              </div>
            )}
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Store Name</Form.Label>
                <Form.Control 
                  type="text"
                  name="shop_title"
                  value={formState.shop_title}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'general-info'}
                  disabled={editingTab !== 'general-info'}
                  className={editingTab !== 'general-info' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Legal Name</Form.Label>
                <Form.Control 
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'general-info'}
                  disabled={editingTab !== 'general-info'}
                  className={editingTab !== 'general-info' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Store URL</Form.Label>
                <InputGroup>
                  <InputGroup.Text id="basic-addon3">
                    .../shop/
                  </InputGroup.Text>
                  <Form.Control 
                    type="text"
                    name="shop_url"
                    value={formState.shop_url}
                    readOnly={true} // URL is always read-only
                    disabled={true}
                    className="bg-light"
                  />
                  <Button 
                    variant="outline-warning"
                    onClick={() => {
                      const url = `${process.env.REACT_APP_SELLER_BASE_URL}/${encodeURIComponent(formState.shop_url)}`;
                      navigator.clipboard.writeText(url);
                      setFeedback({
                        show: true,
                        type: 'success',
                        message: 'URL copied to clipboard'
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </Button>
                  <Button 
                    variant="warning"
                    onClick={() => {
                      const url = `${process.env.REACT_APP_SELLER_BASE_URL}/${encodeURIComponent(formState.shop_url)}`;
                      window.open(url, '_blank');
                    }}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Tax ID/VAT</Form.Label>
                <Form.Control 
                  type="text"
                  name="taxvat"
                  value={formState.taxvat}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'general-info'}
                  disabled={editingTab !== 'general-info'}
                  className={editingTab !== 'general-info' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Store Description</Form.Label>
                <Form.Control 
                  as="textarea"
                  rows={4}
                  name="company_description"
                  value={formState.company_description}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'general-info'}
                  disabled={editingTab !== 'general-info'}
                  className={editingTab !== 'general-info' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )
    },
    {
      id: "contact-address",
      label: "CONTACT & ADDRESS",
      icon: faEnvelope,
      content: (
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Contact Information</h5>
            {editingTab !== 'contact-address' ? (
              <Button 
                variant="outline-warning"
                size="sm"
                onClick={() => startEditing('contact-address')}
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary"
                  size="sm"
                  onClick={cancelEditing}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
                <Button 
                  variant="warning"
                  size="sm"
                  onClick={saveChanges}
                  disabled={Object.keys(modifiedFields).length === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                  )}
                  Save
                </Button>
              </div>
            )}
          </div>
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email Address</Form.Label>
                <Form.Control 
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'contact-address'}
                  disabled={editingTab !== 'contact-address'}
                  className={editingTab !== 'contact-address' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Contact Number</Form.Label>
                <Form.Control 
                  type="text"
                  name="contact_number"
                  value={formState.contact_number}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'contact-address'}
                  disabled={editingTab !== 'contact-address'}
                  className={editingTab !== 'contact-address' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
          </Row>
          
          <h5 className="mb-3">Address Information</h5>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Street Address</Form.Label>
                <Form.Control 
                  type="text"
                  name="billing_street"
                  value={formState.billing_street}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'contact-address'}
                  disabled={editingTab !== 'contact-address'}
                  className={editingTab !== 'contact-address' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>City</Form.Label>
                <Form.Control 
                  type="text"
                  name="billing_city"
                  value={formState.billing_city}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'contact-address'}
                  disabled={editingTab !== 'contact-address'}
                  className={editingTab !== 'contact-address' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Region</Form.Label>
                <Form.Control 
                  type="text"
                  name="billing_region"
                  value={formState.billing_region}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'contact-address'}
                  disabled={editingTab !== 'contact-address'}
                  className={editingTab !== 'contact-address' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Postal Code</Form.Label>
                <Form.Control 
                  type="text"
                  name="billing_postcode"
                  value={formState.billing_postcode}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'contact-address'}
                  disabled={editingTab !== 'contact-address'}
                  className={editingTab !== 'contact-address' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Country</Form.Label>
                <Form.Control 
                  type="text"
                  name="billing_country_id"
                  value={formState.billing_country_id}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'contact-address'}
                  disabled={editingTab !== 'contact-address'}
                  className={editingTab !== 'contact-address' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )
    },
    {
      id: "social-media",
      label: "SOCIAL MEDIA",
      icon: faShareAlt,
      content: (
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Social Media Links</h5>
            {editingTab !== 'social-media' ? (
              <Button 
                variant="outline-warning"
                size="sm"
                onClick={() => startEditing('social-media')}
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary"
                  size="sm"
                  onClick={cancelEditing}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
                <Button 
                  variant="warning"
                  size="sm"
                  onClick={saveChanges}
                  disabled={Object.keys(modifiedFields).length === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                  )}
                  Save
                </Button>
              </div>
            )}
          </div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Twitter</Form.Label>
                <InputGroup>
                  <InputGroup.Text>@</InputGroup.Text>
                  <Form.Control 
                    type="text"
                    name="twitter_id"
                    value={formState.twitter_id}
                    onChange={handleInputChange}
                    readOnly={editingTab !== 'social-media'}
                    disabled={editingTab !== 'social-media'}
                    className={editingTab !== 'social-media' ? 'bg-light' : ''}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Facebook</Form.Label>
                <Form.Control 
                  type="text"
                  name="facebook_id"
                  value={formState.facebook_id}
                  onChange={handleInputChange}
                  placeholder="https://facebook.com/your-page"
                  readOnly={editingTab !== 'social-media'}
                  disabled={editingTab !== 'social-media'}
                  className={editingTab !== 'social-media' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Instagram</Form.Label>
                <InputGroup>
                  <InputGroup.Text>@</InputGroup.Text>
                  <Form.Control 
                    type="text"
                    name="instagram_id"
                    value={formState.instagram_id}
                    onChange={handleInputChange}
                    readOnly={editingTab !== 'social-media'}
                    disabled={editingTab !== 'social-media'}
                    className={editingTab !== 'social-media' ? 'bg-light' : ''}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>YouTube</Form.Label>
                <Form.Control 
                  type="text"
                  name="youtube_id"
                  value={formState.youtube_id}
                  onChange={handleInputChange}
                  placeholder="https://youtube.com/channel/your-channel"
                  readOnly={editingTab !== 'social-media'}
                  disabled={editingTab !== 'social-media'}
                  className={editingTab !== 'social-media' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Pinterest</Form.Label>
                <Form.Control 
                  type="text"
                  name="pinterest_id"
                  value={formState.pinterest_id}
                  onChange={handleInputChange}
                  readOnly={editingTab !== 'social-media'}
                  disabled={editingTab !== 'social-media'}
                  className={editingTab !== 'social-media' ? 'bg-light' : ''}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
      )
    },
    {
      id: "documents",
      label: "DOCUMENTS",
      icon: faIdCard,
      content: (
        <DocumentUploadSection 
          defaultDocUrls={getDocumentUrls()}
          validationStatuses={getValidationStatuses()}
          onDocumentsUploaded={handleDocumentsUploaded}
        />
      )
    },
    {
      id: "policies",
      label: "POLICIES",
      icon: faBook,
      content: (
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Policy Management</h5>
            {editingTab !== 'policies' ? (
              <Button 
                variant="outline-warning"
                size="sm"
                onClick={() => startEditing('policies')}
              >
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Edit
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-secondary"
                  size="sm"
                  onClick={cancelEditing}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Cancel
                </Button>
                <Button 
                  variant="warning"
                  size="sm"
                  onClick={saveChanges}
                  disabled={Object.keys(modifiedFields).length === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  ) : (
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                  )}
                  Save
                </Button>
              </div>
            )}
          </div>
          <Tab.Container defaultActiveKey="return-policy">
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="return-policy">Return Policy</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="shipping-policy">Shipping Policy</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="privacy-policy">Privacy Policy</Nav.Link>
              </Nav.Item>
            </Nav>
            
            <div className="py-3">
              <Tab.Content>
                <Tab.Pane eventKey="return-policy">
                  <Form.Group>
                    <Form.Label>Return Policy</Form.Label>
                    <Form.Control 
                      as="textarea"
                      rows={6}
                      name="return_policy"
                      value={formState.return_policy}
                      onChange={handleInputChange}
                      placeholder="Describe your return policy..."
                      readOnly={editingTab !== 'policies'}
                      disabled={editingTab !== 'policies'}
                      className={editingTab !== 'policies' ? 'bg-light' : ''}
                    />
                  </Form.Group>
                </Tab.Pane>
                <Tab.Pane eventKey="shipping-policy">
                  <Form.Group>
                    <Form.Label>Shipping Policy</Form.Label>
                    <Form.Control 
                      as="textarea"
                      rows={6}
                      name="shipping_policy"
                      value={formState.shipping_policy}
                      onChange={handleInputChange}
                      placeholder="Describe your shipping policy..."
                      readOnly={editingTab !== 'policies'}
                      disabled={editingTab !== 'policies'}
                      className={editingTab !== 'policies' ? 'bg-light' : ''}
                    />
                  </Form.Group>
                </Tab.Pane>
                <Tab.Pane eventKey="privacy-policy">
                  <Form.Group>
                    <Form.Label>Privacy Policy</Form.Label>
                    <Form.Control 
                      as="textarea"
                      rows={6}
                      name="privacy_policy"
                      value={formState.privacy_policy}
                      onChange={handleInputChange}
                      placeholder="Describe your privacy policy..."
                      readOnly={editingTab !== 'policies'}
                      disabled={editingTab !== 'policies'}
                      className={editingTab !== 'policies' ? 'bg-light' : ''}
                    />
                  </Form.Group>
                </Tab.Pane>
              </Tab.Content>
            </div>
          </Tab.Container>
        </div>
      )
    },
  ];

  return (
    <Card>
      <Card.Body>
        {/* Feedback alert */}
        {feedback.show && (
          <Alert
            variant={feedback.type}
            onClose={() => setFeedback(prev => ({ ...prev, show: false }))}
            dismissible
          >
            {feedback.message}
          </Alert>
        )}

        <Tab.Container defaultActiveKey="general-info">
          {/* Tabs Navigation */}
          {/* Orange focus color for tabs with custom style */}
          <Nav className="mb-4 pb-1 gap-3 nav-underline flex-nowrap scrollbar">
            <style>
              {`
                .nav-underline .nav-link.active {
                  color: #fd7e14 !important; /* Bootstrap orange */
                  border-bottom-color: #fd7e14 !important;
                }
                .nav-link:hover, .nav-link:focus {
                  color: #fd7e14 !important;
                }
              `}
            </style>
            {tabItems.map((item) => (
              <Nav.Item key={item.id}>
                <Nav.Link eventKey={item.id} className="text-nowrap">
                  <FontAwesomeIcon icon={item.icon} className="me-2" />
                  {item.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          {/* Tab Content */}
          <Tab.Content>
            {tabItems.map((item) => (
              <Tab.Pane key={item.id} eventKey={item.id}>
                <div className="form-content">
                  {item.content}
                </div>
              </Tab.Pane>
            ))}
          </Tab.Content>
        </Tab.Container>
      </Card.Body>
    </Card>
  );
};

// Wrapper components for each form component to provide the change callback
// These are unchanged from the original SellerProfileEditor

interface WrapperProps {
  onFieldChange: (fieldName: string, value: any) => void;
  onUpdateSellerInfo?: (updatedTitle: string, updatedDescription: string) => void;
}

// SellerProfileInfo wrapper
const SellerProfileInfoWrapper: React.FC<WrapperProps> = ({ onFieldChange, onUpdateSellerInfo }) => {
  const { seller } = useAuth();
  const [description, setDescription] = useState(seller?.company_description || '');
  const [shopTitle, setShopTitle] = useState(seller?.shop_title || '');

  // Track changes and notify parent
  useEffect(() => {
    if (description !== seller?.company_description) {
      onFieldChange('company_description', description);
    }
  }, [description, seller?.company_description, onFieldChange]);

  useEffect(() => {
    if (shopTitle !== seller?.shop_title) {
      onFieldChange('shop_title', shopTitle);
    }
  }, [shopTitle, seller?.shop_title, onFieldChange]);

  // Pass custom handlers to child component
  const customProps = {
    onUpdateSellerInfo: onUpdateSellerInfo || (() => { }),
    // Pass current state values to the child
    externalDescription: description,
    externalShopTitle: shopTitle,
    // Override the component's internal state handlers
    customHandlers: {
      handleDescriptionChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setDescription(value);
      },
      handleShopTitleChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setShopTitle(event.target.value);
      }
    }
  };

  return <SellerProfileInfo {...customProps} />;
};

// SellerBankInfo wrapper
const SellerBankInfoWrapper: React.FC<WrapperProps> = ({ onFieldChange }) => {
  const { seller } = useAuth();
  const [paymentSource, setPaymentSource] = useState(seller?.payment_source || '');

  // Track changes and notify parent
  useEffect(() => {
    if (paymentSource !== seller?.payment_source) {
      onFieldChange('payment_source', paymentSource);
    }
  }, [paymentSource, seller?.payment_source, onFieldChange]);

  // Create custom handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentSource(e.target.value);
  };

  // Pass our custom handlers to override the component's internal ones
  const customProps = {
    externalPaymentSource: paymentSource,
    customHandlers: {
      handleInputChange
    }
  };

  return <SellerBankInfo {...customProps} />;
};

// SellerReturnPolicy wrapper
const SellerReturnPolicyWrapper: React.FC<WrapperProps> = ({ onFieldChange }) => {
  const { seller } = useAuth();
  const [returnPolicy, setReturnPolicy] = useState(seller?.return_policy || '');

  // Track changes and notify parent
  useEffect(() => {
    if (returnPolicy !== seller?.return_policy) {
      onFieldChange('return_policy', returnPolicy);
    }
  }, [returnPolicy, seller?.return_policy, onFieldChange]);

  // Create custom handlers
  const handleReturnPolicyChange = (content: string) => {
    setReturnPolicy(content);
  };

  // Pass our custom handlers to override the component's internal ones
  const customProps = {
    externalReturnPolicy: returnPolicy,
    customHandlers: {
      handleReturnPolicyChange
    }
  };

  return <SellerReturnPolicy {...customProps} />;
};

// SellerShippingPolicy wrapper
const SellerShippingPolicyWrapper: React.FC<WrapperProps> = ({ onFieldChange }) => {
  const { seller } = useAuth();
  const [shippingPolicy, setShippingPolicy] = useState(seller?.shipping_policy || '');

  // Track changes and notify parent
  useEffect(() => {
    if (shippingPolicy !== seller?.shipping_policy) {
      onFieldChange('shipping_policy', shippingPolicy);
    }
  }, [shippingPolicy, seller?.shipping_policy, onFieldChange]);

  // Create custom handlers
  const handleShippingPolicyChange = (content: string) => {
    setShippingPolicy(content);
  };

  // Pass our custom handlers to override the component's internal ones
  const customProps = {
    externalShippingPolicy: shippingPolicy,
    customHandlers: {
      handleShippingPolicyChange
    }
  };

  return <SellerShippingPolicy {...customProps} />;
};

export default SellerProfileEditorWithDocs;
